import { VIZ_TOOL_NAME } from "@/lib/ai/visualization/config";
import { isVisualizationSourceToolName } from "@/lib/ai/visualization/data/adapters/adapter-registry";
import { buildVizDatasetOutput } from "@/lib/ai/visualization/data/pipeline/dataset-output";
import type {
  VizDataset,
  VizDatasetOutput,
} from "@/lib/ai/visualization/data/schema";
import { resolveVizFollowupContext } from "@/lib/ai/visualization/narration/followup-context";
import {
  isRenderableVizResultPayload,
  normalizeVisualizationTransportValue,
  type ScannedVisualizationPart,
  type ScannedVisualizationToolPart,
  scanVisualizationMessageParts,
  type VizMessageLike,
} from "@/lib/ai/visualization/shared/transport";
import type {
  VizRenderDataPartPayload,
  VizRenderOutput,
} from "@/lib/ai/visualization/types";

/**
 * 可視化 dataset 再構成に使う source tool snapshot。
 *
 * @remarks
 * `viz_render` 自身の出力は含めず、adapter が再解釈できる input/output だけを保持する。
 */
export type VizToolSnapshot = {
  toolCallId: string;
  toolName: string;
  input: unknown | null;
  output: unknown | null;
};

type ToolEntry = {
  toolCallId: string;
  toolName: string;
  input: unknown | null;
  output: unknown | null;
  scanOrder: number;
};

type CompletedToolSnapshotOptions = {
  includeToolName?: (toolName: string) => boolean;
};

/**
 * 可視化 source tool snapshot を履歴復元と live capture の両方で同じ形へそろえる。
 *
 * @remarks
 * TOON 文字列が混在すると downstream の dataset 再構成で分岐が増えるため、
 * 保存時点で decode 済みの `unknown | null` へ正規化しておく。
 */
export function normalizeVizToolSnapshot(
  snapshot: VizToolSnapshot,
): VizToolSnapshot {
  return {
    ...snapshot,
    input: normalizeVisualizationTransportValue(snapshot.input) ?? null,
    output: normalizeVisualizationTransportValue(snapshot.output) ?? null,
  };
}

function ensureToolEntry(args: {
  byToolCallId: Map<string, ToolEntry>;
  toolCallId: string;
  toolName: string;
  scanOrder: number;
}): ToolEntry {
  const existing = args.byToolCallId.get(args.toolCallId);
  if (existing) return existing;

  const created: ToolEntry = {
    toolCallId: args.toolCallId,
    toolName: args.toolName,
    input: null,
    output: null,
    scanOrder: args.scanOrder,
  };
  args.byToolCallId.set(args.toolCallId, created);
  return created;
}

function applyToolPartToEntry(args: {
  entry: ToolEntry;
  part: ScannedVisualizationToolPart;
  scanOrder: number;
}): ToolEntry {
  const next: ToolEntry = { ...args.entry };

  if (
    (args.part.state === "input-streaming" ||
      args.part.state === "input-available") &&
    args.part.input !== undefined
  ) {
    next.input = args.part.input ?? null;
  }

  if (
    args.part.state === "output-available" &&
    args.part.input !== undefined &&
    next.input === null
  ) {
    next.input = args.part.input ?? null;
  }

  if (
    args.part.state === "output-available" &&
    args.part.output !== undefined
  ) {
    next.output = args.part.output ?? null;
    next.scanOrder = args.scanOrder;
  }

  return next;
}

function upsertToolEntryFromPart(args: {
  byToolCallId: Map<string, ToolEntry>;
  part: ScannedVisualizationToolPart;
  toolCallId: string;
  toolName: string;
  scanOrder: number;
}): void {
  const entry = ensureToolEntry({
    byToolCallId: args.byToolCallId,
    toolCallId: args.toolCallId,
    toolName: args.toolName,
    scanOrder: args.scanOrder,
  });
  args.byToolCallId.set(
    args.toolCallId,
    applyToolPartToEntry({
      entry,
      part: args.part,
      scanOrder: args.scanOrder,
    }),
  );
}

/**
 * scan 済み part 群から source tool snapshot を復元する。
 *
 * @remarks
 * 同一 toolCallId は後勝ちで統合し、`viz_render` 自身は除外する。
 * history 復元と client 側の dataset 再構成で同じ規則を共有するため、
 * decode / scan order / completed-only の条件をこの関数へ集約する。
 */
export function buildVizSourceSnapshotsFromScannedParts(
  parts: readonly ScannedVisualizationPart[],
  shouldCapture: (toolName: string) => boolean = isVisualizationSourceToolName,
): VizToolSnapshot[] {
  const byToolCallId = new Map<string, ToolEntry>();

  parts.forEach((part) => {
    if (part.kind !== "tool") return;
    if (!shouldCapture(part.toolName)) return;
    if (!part.toolCallId) return;

    upsertToolEntryFromPart({
      byToolCallId,
      part,
      toolCallId: part.toolCallId,
      toolName: part.toolName,
      scanOrder: part.scanOrder,
    });
  });

  return collectCompletedToolSnapshots(byToolCallId, {
    includeToolName: shouldCapture,
  });
}

/**
 * 現在までに完了した tool entry 群を、dataset 再構成が読める snapshot 配列へ変換する。
 *
 * @remarks
 * 各 `viz_render` は「その時点までに完了済みだった source tool」だけを参照すべきため、
 * history 復元でも scan 中の中間状態をそのまま snapshot 化できる helper を分離する。
 */
function collectCompletedToolSnapshots(
  byToolCallId: ReadonlyMap<string, ToolEntry>,
  options: CompletedToolSnapshotOptions = {},
): VizToolSnapshot[] {
  const includeToolName = options.includeToolName ?? (() => true);

  return [...byToolCallId.values()]
    .filter((entry) => entry.output !== null && includeToolName(entry.toolName))
    .sort((left, right) => left.scanOrder - right.scanOrder)
    .map((entry) =>
      normalizeVizToolSnapshot({
        toolCallId: entry.toolCallId,
        toolName: entry.toolName,
        input: entry.input,
        output: entry.output,
      }),
    );
}

function appendDatasetsToIndex(
  datasetIndex: Map<string, Map<string, VizDataset>>,
  scope: string,
  output: VizDatasetOutput,
): void {
  const scopedDatasets =
    datasetIndex.get(scope) ?? new Map<string, VizDataset>();
  Object.entries(output.datasets).forEach(([datasetId, dataset]) => {
    scopedDatasets.set(datasetId, dataset);
  });
  datasetIndex.set(scope, scopedDatasets);
}

/**
 * その時点までに完成していた source snapshots から、対象 viz card の dataset scope を復元する。
 *
 * @remarks
 * `data-viz-result` は tool result より先に届くことがあるため、tool part の
 * `output-available` だけを待つと live card の data 解決が 1 step 遅れる。
 * data-part / tool-part のどちらであっても、toolCallId が確定した時点で同じ規則で
 * dataset index を更新する。
 */
function appendHistoryDatasetsForVizToolCall(args: {
  datasetIndex: Map<string, Map<string, VizDataset>>;
  byToolCallId: ReadonlyMap<string, ToolEntry>;
  toolCallId: string;
}): void {
  const snapshots = collectCompletedToolSnapshots(args.byToolCallId, {
    includeToolName: isVisualizationSourceToolName,
  });
  const output = buildVizDatasetOutput(snapshots, {
    toolCallId: args.toolCallId,
  });
  if (Object.keys(output.datasets).length === 0) {
    return;
  }
  appendDatasetsToIndex(args.datasetIndex, args.toolCallId, output);
}

/**
 * scan 済み part 群から、toolCallId ごとの dataset index を復元する。
 *
 * @remarks
 * dataset raw は source tool output を正本にし、`viz_render` の toolCallId ごとに
 * `buildVizDatasetOutput` を再実行して refresh 後の再描画を維持する。
 */
export function buildVizHistoryDatasetIndexFromScannedParts(
  parts: readonly ScannedVisualizationPart[],
): Map<string, Map<string, VizDataset>> {
  const datasetIndex = new Map<string, Map<string, VizDataset>>();
  const byToolCallId = new Map<string, ToolEntry>();

  parts.forEach((part) => {
    if (part.kind === "tool") {
      if (isVisualizationSourceToolName(part.toolName) && part.toolCallId) {
        upsertToolEntryFromPart({
          byToolCallId,
          part,
          toolCallId: part.toolCallId,
          toolName: part.toolName,
          scanOrder: part.scanOrder,
        });
        return;
      }

      if (
        part.toolName === VIZ_TOOL_NAME &&
        part.state === "output-available" &&
        part.toolCallId
      ) {
        appendHistoryDatasetsForVizToolCall({
          datasetIndex,
          byToolCallId,
          toolCallId: part.toolCallId,
        });
      }
      return;
    }

    if (!part.payload.toolCallId) return;
    appendHistoryDatasetsForVizToolCall({
      datasetIndex,
      byToolCallId,
      toolCallId: part.payload.toolCallId,
    });
  });

  return datasetIndex;
}

export type BuildVisualizationHistoryModelOptions = {
  /**
   * source snapshot として採用する tool を絞り込む。
   *
   * @remarks
   * shared scan や dataset 再構成規則を分岐させずに、呼び出し側だけで
   * 対象 tool を狭めたいときに使う。
   */
  shouldCaptureSnapshot?: (toolName: string) => boolean;
};

/**
 * 1 回の visualization history scan から組み立てる共有 read model。
 *
 * @remarks
 * runtime と hooks が毎回 ad hoc に再組み立てしなくて済むよう、
 * 派生 index と follow-up 用 facts を 1 つの facade にまとめる。
 */
export type VisualizationHistoryModel = {
  scannedParts: readonly ScannedVisualizationPart[];
  vizRenderDataPayloads: VizRenderDataPartPayload[];
  vizOutputIndex: Map<string, VizRenderOutput>;
  datasetIndex: Map<string, Map<string, VizDataset>>;
  sourceSnapshots: VizToolSnapshot[];
  recentVisualizations: Array<NonNullable<VizRenderOutput["followupContext"]>>;
};

function forEachVizRenderDataPayload(
  parts: readonly ScannedVisualizationPart[],
  visit: (payload: VizRenderDataPartPayload) => void,
): void {
  parts.forEach((part) => {
    if (part.kind === "viz-result-data") {
      visit(part.payload);
      return;
    }
    if (!part.vizResultPayload) return;
    visit(part.vizResultPayload);
  });
}

function buildVizRenderOutputIndexFromPayloads(
  payloads: readonly VizRenderDataPartPayload[],
): Map<string, VizRenderOutput> {
  const index = new Map<string, VizRenderOutput>();

  payloads.forEach((payload) => {
    // Ordinary failures belong to Process details. A validated table
    // substitution is still a useful render result and remains restorable.
    if (!payload.toolCallId || !isRenderableVizResultPayload(payload)) return;
    index.set(payload.toolCallId, payload.output);
  });

  return index;
}

function collectVizRenderDataPayloadsFromScannedParts(
  parts: readonly ScannedVisualizationPart[],
): VizRenderDataPartPayload[] {
  const payloads: VizRenderDataPartPayload[] = [];

  forEachVizRenderDataPayload(parts, (payload) => {
    payloads.push(payload);
  });

  return payloads;
}

/**
 * visualization message 群を scan し、共有 history facade を返す。
 *
 * @remarks
 * 呼び出し側が scan helper を手作業で組み合わせると再構成規則が drift しやすい。
 * runtime・hooks・follow-up context 生成で同じ history 契約を保つため、
 * 入口はこの関数へ寄せる。
 */
export function buildVisualizationHistoryModel(
  messages: readonly VizMessageLike[],
  options: BuildVisualizationHistoryModelOptions = {},
): VisualizationHistoryModel {
  return buildVisualizationHistoryModelFromScannedParts(
    scanVisualizationMessageParts(messages),
    options,
  );
}

/**
 * 既に scan 済みの visualization part 群から共有 history facade を組み立てる。
 *
 * @remarks
 * scan 済み part を再利用できる呼び出し元では、この入口を使って二重 scan を避ける。
 * 返す派生 index と snapshot 規則は `buildVisualizationHistoryModel()` と同一である。
 */
export function buildVisualizationHistoryModelFromScannedParts(
  parts: readonly ScannedVisualizationPart[],
  options: BuildVisualizationHistoryModelOptions = {},
): VisualizationHistoryModel {
  const scannedParts = [...parts];
  const vizRenderDataPayloads =
    collectVizRenderDataPayloadsFromScannedParts(scannedParts);

  return {
    scannedParts,
    vizRenderDataPayloads,
    vizOutputIndex: buildVizRenderOutputIndexFromPayloads(
      vizRenderDataPayloads,
    ),
    datasetIndex: buildVizHistoryDatasetIndexFromScannedParts(scannedParts),
    sourceSnapshots: buildVizSourceSnapshotsFromScannedParts(
      scannedParts,
      options.shouldCaptureSnapshot,
    ),
    recentVisualizations: vizRenderDataPayloads.flatMap((payload) => {
      if (!isRenderableVizResultPayload(payload)) return [];
      // follow-up facts は current contract に合う payload だけを採用する。
      // 壊れた履歴をそのまま main LLM へ渡すと continuity よりクラッシュが先に起きる。
      const followupContext = resolveVizFollowupContext(
        payload.output.followupContext,
      );
      return followupContext ? [followupContext] : [];
    }),
  };
}
